









import { Component, Prop, Vue } from "@/shared/vuepack";
import {
  GenericBarChartDataPoint, GenericChartValueType, GenericMultiColumnBarChartDataPoint
} from "@/shared/types";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";
import { formatCurrency } from "@/shared/currency";
import { roundToTwoDecimals } from "@/shared/mathHelper";

@Component({
  components: {
    GChart
  },
})
export default class MultiColumnGenericBarChart extends Vue {
  @Prop({ default: 220 })
  height: number;

  @Prop({ required: false })
  vAxisTitle: string;

  @Prop({ required: false })
  hAxisTitle: string;

  @Prop({ required: true })
  valueLabel: string;

  @Prop({ required: true })
  valueType: GenericChartValueType;

  @Prop({ required: false })
  dateTooltipLabel: string;

  @Prop({ required: true })
  dateFormatString: string;

  @Prop({ required: true })
  dataSet: GenericMultiColumnBarChartDataPoint[];

  themeStore = useThemeStore();

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor,
        stem: {
          length: 0
        },
        textStyle: {
          fontSize: 12,
          opacity: this.annotationOpacity
        }
      },
      titlePosition: "none",
      chartArea: {
        top: 15,
        right: 25,
        left: 110,
        height: "80%",
      },
      focusTarget: "datum",
      colors: [this.themeStore.tertiaryColor, this.themeStore.primaryColor],
      tooltip: {
        isHtml: true,
      },

      hAxis: {
        title: this.hAxisTitle,
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        gridlines: {
          color: "transparent"
        },
        slantedText: false,
        baselineColor: "black",
      },
      vAxis: {
        title: this.vAxisTitle,
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        baselineColor: "black",
        format: this.vAxisFormat,
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  get annotationOpacity (): number {
    return window.innerWidth > 500 ? 1 : 0;
  }

  get vAxisFormat (): string {
    if (this.valueType === GenericChartValueType.Percentage) {
      return `#'%'`;
    } else if (this.valueType === GenericChartValueType.Dollar) {
      return "currency";
    }

    return "##";
  }

  formattedValueForTooltip (value: number): string {
    if (this.valueType === GenericChartValueType.Dollar) {
      return formatCurrency(value);
    } else if (this.valueType === GenericChartValueType.Percentage) {
      return `${roundToTwoDecimals(value).toFixed(2)}%`;
    }

    return `${value}`;
  }

  isPositiveNumber (value: number): boolean {
    return value >= 0;
  }

  getColorForDataValue (value: number): string {
    return this.isPositiveNumber(value) ?
        "#32A26E" : "#D6422B";
  }

  getColorForDataValueDarker (value: number): string {
    return this.isPositiveNumber(value) ?
        "#195137" : "#6B2116";
  }

  benchmarkTooltipFormatter (data: GenericMultiColumnBarChartDataPoint): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          Benchmark Performance
                        </div>
                        <div margin-bottom: 3px;">
                          Rate of Return
                        </div>
                        <div style="color: ${this.getColorForDataValue(data.value1)}; font-weight: bold;">
                          ${this.formattedValueForTooltip(data.value1)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  trailingTooltipFormatter (data: GenericMultiColumnBarChartDataPoint): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          Trailing Performance
                        </div>
                        <div margin-bottom: 3px;">
                          Rate of Return
                        </div>
                        <div style="color: ${this.getColorForDataValue(data.value2)}; font-weight: bold;">
                          ${this.formattedValueForTooltip(data.value2)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  formatChartDataItem (chartPoint: GenericMultiColumnBarChartDataPoint): any {
    const barColor = this.getColorForDataValue(chartPoint.value1);
    const barColorDarker = this.getColorForDataValueDarker(chartPoint.value2);
    return [
      chartPoint.label,
      chartPoint.value1,
      this.benchmarkTooltipFormatter(chartPoint),
      `stroke-color: ${barColor}; color: ${barColor};`,
      `${roundToTwoDecimals(chartPoint.value1).toFixed(2)}%`,
      chartPoint.value2,
      this.trailingTooltipFormatter(chartPoint),
      `stroke-color: ${barColorDarker}; color: ${barColorDarker}`,
      `${roundToTwoDecimals(chartPoint.value2).toFixed(2)}%`
    ];
  }

  get formattedChartData (): any {
    const formattedChartPoints = this.dataSet.map(this.formatChartDataItem);

    return [
      [
        "Time Period",
        "Benchmark Performance",
        { type: "string", role: "tooltip", p: { html: true } },
        { type: "string", role: "style" },
        { type: "string", role: "annotation" },
        "Trailing Performance",
        { type: "string", role: "tooltip", p: { html: true } },
        { type: "string", role: "style" },
        { type: "string", role: "annotation" }
  ],

      ...formattedChartPoints
    ];
  }
}
